// Knowledge Base — record detail / edit page.
//
// Renders a dynamic form from the schema returned by /api/kb/databases.
// Supports inline AI actions (summarize, recommend-vendors / -programs,
// competitive-threats depending on which DB this record belongs to).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Trash2,
  Sparkles,
  Loader2,
  Brain,
  TrendingUp,
  Network,
  Layers,
  Radar,
  X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DB_BY_KEY } from "./kbConfig";

function FieldRenderer({ field, value, onChange }) {
  const handleChange = (e) => onChange(e.target.value);
  const v = value ?? (field.kind === "bool" ? false : field.kind === "tags" || field.kind === "attachments" ? [] : "");
  const base =
    "bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-500";

  if (field.kind === "textarea") {
    return (
      <Textarea
        data-testid={`kb-field-${field.name}`}
        value={v}
        onChange={handleChange}
        rows={4}
        className={base}
      />
    );
  }
  if (field.kind === "select") {
    return (
      <select
        data-testid={`kb-field-${field.name}`}
        value={v}
        onChange={handleChange}
        className={`rounded-md border px-3 py-2 text-sm ${base} w-full`}
      >
        <option value="">—</option>
        {(field.options || []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (field.kind === "bool") {
    return (
      <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-200">
        <input
          data-testid={`kb-field-${field.name}`}
          type="checkbox"
          checked={!!v}
          onChange={(e) => onChange(e.target.checked)}
          className="accent-sky-500"
        />
        Yes
      </label>
    );
  }
  if (field.kind === "scale") {
    const max = field.scale || 10;
    return (
      <div className="flex items-center gap-3">
        <Input
          data-testid={`kb-field-${field.name}`}
          type="number"
          min={0}
          max={max}
          value={v === "" ? "" : v}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className={`max-w-[100px] ${base}`}
        />
        <span className="text-xs text-slate-400">/ {max}</span>
      </div>
    );
  }
  if (field.kind === "number" || field.kind === "currency") {
    return (
      <Input
        data-testid={`kb-field-${field.name}`}
        type="number"
        value={v === "" ? "" : v}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className={`max-w-[200px] ${base}`}
      />
    );
  }
  if (field.kind === "tags") {
    return (
      <TagInput
        testId={`kb-field-${field.name}`}
        value={Array.isArray(v) ? v : []}
        onChange={onChange}
      />
    );
  }
  if (field.kind === "url") {
    return (
      <Input
        data-testid={`kb-field-${field.name}`}
        type="url"
        value={v}
        onChange={handleChange}
        placeholder="https://"
        className={base}
      />
    );
  }
  if (field.kind === "date") {
    return (
      <Input
        data-testid={`kb-field-${field.name}`}
        type="date"
        value={v}
        onChange={handleChange}
        className={`max-w-[220px] ${base}`}
      />
    );
  }
  if (field.kind === "attachments") {
    // Simple URL list for MVP.
    return (
      <TagInput
        testId={`kb-field-${field.name}`}
        value={Array.isArray(v) ? v : []}
        onChange={onChange}
        placeholder="Paste a URL and press Enter"
      />
    );
  }
  return (
    <Input
      data-testid={`kb-field-${field.name}`}
      value={v}
      onChange={handleChange}
      className={base}
    />
  );
}

function TagInput({ value, onChange, testId, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (value.includes(t)) return;
    onChange([...value, t]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-xs text-slate-200"
          >
            {t}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== t))}
              className="text-slate-400 hover:text-rose-400"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          data-testid={testId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder || "Type and press Enter…"}
          className="bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-500"
        />
        <Button type="button" variant="outline" onClick={add} className="bg-slate-900/40 border-white/10 text-slate-200">
          Add
        </Button>
      </div>
    </div>
  );
}

export default function AdminKnowledgeBaseRecord() {
  const { dbKey, recordId } = useParams();
  const navigate = useNavigate();
  const dbMeta = DB_BY_KEY[dbKey];

  const [schema, setSchema] = useState(null);
  const [record, setRecord] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [aiResult, setAiResult] = useState(null);

  useEffect(() => {
    if (!dbMeta) {
      navigate("/admin/knowledge-base");
      return;
    }
    setLoading(true);
    Promise.all([
      api.get("/kb/databases"),
      api.get(`/kb/${dbKey}/${recordId}`),
    ])
      .then(([r1, r2]) => {
        const s = (r1.data || []).find((d) => d.key === dbKey);
        setSchema(s);
        setRecord(r2.data);
        setDraft(r2.data);
      })
      .catch((e) => {
        toast.error(formatApiError(e, "Could not load record"));
        navigate(`/admin/knowledge-base/${dbKey}`);
      })
      .finally(() => setLoading(false));
  }, [dbKey, recordId, dbMeta, navigate]);

  const updateField = (name, val) => {
    setDraft((d) => ({ ...d, [name]: val }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/kb/${dbKey}/${recordId}`, draft);
      setRecord(r.data);
      setDraft(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this ${dbMeta.label} record? This cannot be undone.`)) return;
    try {
      await api.delete(`/kb/${dbKey}/${recordId}`);
      toast.success("Deleted");
      navigate(`/admin/knowledge-base/${dbKey}`);
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  const runAi = async (action) => {
    setAiBusy(action);
    setAiResult(null);
    try {
      if (action === "summarize") {
        const r = await api.post("/kb/ai/summarize", { db_key: dbKey, record_id: recordId });
        // Reflect summary in form
        if (r.data.field) {
          setDraft((d) => ({ ...d, [r.data.field]: r.data.summary }));
        }
        setAiResult({ type: "summary", text: r.data.summary, field: r.data.field });
        toast.success("AI summary generated");
      } else if (action === "recommend-vendors") {
        const r = await api.post("/kb/ai/recommend-vendors", { record_id: recordId });
        setAiResult({ type: "vendor-recs", recommendations: r.data.recommendations || [] });
      } else if (action === "recommend-programs") {
        const r = await api.post("/kb/ai/recommend-programs", { record_id: recordId });
        setAiResult({ type: "program-recs", recommendations: r.data.recommendations || [] });
      } else if (action === "competitive-threats") {
        const r = await api.post("/kb/ai/competitive-threats", { record_id: recordId });
        setAiResult({ type: "threats", threats: r.data.threats || [] });
      }
    } catch (e) {
      toast.error(formatApiError(e, "AI action failed"));
    } finally {
      setAiBusy(null);
    }
  };

  if (loading || !dbMeta || !schema || !record) {
    return (
      <div className="min-h-screen bg-slate-950 p-10 text-sm text-slate-300">
        <Loader2 className="inline mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const Icon = dbMeta.icon;
  const cls = dbMeta.cls;
  const aiActions = dbMeta.ai || [];

  return (
    <div
      data-testid={`kb-record-${recordId}`}
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 space-y-6"
    >
      <Link
        to={`/admin/knowledge-base/${dbKey}`}
        data-testid="kb-record-back"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft className="h-3 w-3" /> {dbMeta.label}
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-[10.5px] uppercase tracking-[0.16em] font-semibold ${cls.text} flex items-center gap-1.5`}>
            <Icon className="h-3 w-3" /> {dbMeta.label}
          </div>
          <h1 className="mt-1 text-3xl font-bold text-slate-100 truncate">
            {draft[dbMeta.primary] || "(untitled)"}
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            ID {record.id}
            {record.source && record.source_id && (
              <> · linked from {record.source}:{record.source_id}</>
            )}
            {record.updated_at && <> · last updated {new Date(record.updated_at).toLocaleString()}</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {aiActions.includes("summarize") && (
            <Button
              data-testid="kb-ai-summarize"
              variant="outline"
              onClick={() => runAi("summarize")}
              disabled={aiBusy !== null}
              className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
            >
              {aiBusy === "summarize" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />}
              AI Summarize
            </Button>
          )}
          {aiActions.includes("recommend-vendors") && (
            <Button
              data-testid="kb-ai-recommend-vendors"
              variant="outline"
              onClick={() => runAi("recommend-vendors")}
              disabled={aiBusy !== null}
              className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
            >
              {aiBusy === "recommend-vendors" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Network className="mr-1 h-3.5 w-3.5" />}
              Recommend Vendors
            </Button>
          )}
          {aiActions.includes("recommend-programs") && (
            <Button
              data-testid="kb-ai-recommend-programs"
              variant="outline"
              onClick={() => runAi("recommend-programs")}
              disabled={aiBusy !== null}
              className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
            >
              {aiBusy === "recommend-programs" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Layers className="mr-1 h-3.5 w-3.5" />}
              Recommend Programs
            </Button>
          )}
          {aiActions.includes("competitive-threats") && (
            <Button
              data-testid="kb-ai-threats"
              variant="outline"
              onClick={() => runAi("competitive-threats")}
              disabled={aiBusy !== null}
              className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
            >
              {aiBusy === "competitive-threats" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Radar className="mr-1 h-3.5 w-3.5" />}
              Threat Scan
            </Button>
          )}
          <Button data-testid="kb-record-save" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
            Save
          </Button>
          <Button
            data-testid="kb-record-delete"
            variant="outline"
            onClick={remove}
            className="border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
          </Button>
        </div>
      </header>

      {aiResult && (
        <section
          data-testid="kb-ai-result"
          className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-sky-200 flex items-center gap-2">
              <Brain className="h-4 w-4" /> Claude Sonnet 4.5 ·{" "}
              {aiResult.type === "summary"
                ? "AI Summary"
                : aiResult.type === "vendor-recs"
                ? "Vendor Recommendations"
                : aiResult.type === "program-recs"
                ? "Program Recommendations"
                : "Competitive Threats"}
            </h3>
            <button
              onClick={() => setAiResult(null)}
              className="text-xs text-slate-400 hover:text-slate-200"
            >
              dismiss
            </button>
          </div>
          {aiResult.type === "summary" && (
            <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-100">{aiResult.text}</p>
          )}
          {(aiResult.type === "vendor-recs" || aiResult.type === "program-recs") && (
            <ul className="mt-3 space-y-2">
              {aiResult.recommendations.map((r, i) => {
                const isVendor = aiResult.type === "vendor-recs";
                const title = isVendor ? r.vendor_name : r.program_name;
                const link = isVendor && r.vendor_id ? `/admin/knowledge-base/vendors/${r.vendor_id}` : !isVendor && r.program_id ? `/admin/knowledge-base/programs/${r.program_id}` : null;
                return (
                  <li key={`${title}-${i}`} className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        {link ? (
                          <Link to={link} className="text-sm font-semibold text-sky-300 hover:underline">
                            {title}
                          </Link>
                        ) : (
                          <span className="text-sm font-semibold text-slate-100">{title}</span>
                        )}
                      </div>
                      {typeof r.fit_score === "number" && (
                        <span className="rounded-full bg-emerald-500/15 text-emerald-300 px-2 py-0.5 text-[10px] font-semibold">
                          Fit {r.fit_score}/100
                        </span>
                      )}
                    </div>
                    {r.why && <p className="mt-1 text-xs text-slate-300">{r.why}</p>}
                    {r.risk && <p className="mt-1 text-[11px] text-amber-300">Risk: {r.risk}</p>}
                    {r.first_step && <p className="mt-1 text-[11px] text-sky-300">First step: {r.first_step}</p>}
                  </li>
                );
              })}
            </ul>
          )}
          {aiResult.type === "threats" && (
            <ul className="mt-3 space-y-2">
              {aiResult.threats.map((t, i) => (
                <li key={`${t.competitor}-${i}`} className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-slate-100">{t.competitor}</span>
                    <span className="rounded-full bg-rose-500/15 text-rose-300 px-2 py-0.5 text-[10px] font-semibold">
                      {t.threat_level}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-300">{t.summary}</p>
                  {t.counter && <p className="mt-1 text-[11px] text-emerald-300">Counter: {t.counter}</p>}
                  {t.action && <p className="mt-1 text-[11px] text-sky-300">Action: {t.action}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Form */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(schema?.fields || []).map((f) => (
          <div
            key={f.name}
            className={`rounded-2xl border border-white/10 bg-slate-950/60 p-4 ${
              f.kind === "textarea" || f.kind === "tags" || f.kind === "attachments" ? "lg:col-span-2" : ""
            }`}
          >
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              {f.label}
              {f.required && <span className="text-rose-400 ml-0.5">*</span>}
              {f.ai && <span className="ml-2 inline-flex items-center gap-0.5 text-[9px] text-sky-300"><Sparkles className="h-2.5 w-2.5" /> AI</span>}
            </label>
            <FieldRenderer
              field={f}
              value={draft[f.name]}
              onChange={(v) => updateField(f.name, v)}
            />
          </div>
        ))}
        <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4 lg:col-span-2">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
            Tags
          </label>
          <TagInput
            testId="kb-field-tags"
            value={Array.isArray(draft.tags) ? draft.tags : []}
            onChange={(v) => updateField("tags", v)}
          />
        </div>
      </section>
    </div>
  );
}
